import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Per-page SEO. Model B: a stored `page_seo` record (keyed by path) is the
 * OFFICIAL source — when present it overrides the generated props below.
 * Generated values are only a fallback when no record exists.
 *
 * <title>, robots and JSON-LD are managed imperatively in <head> (react-helmet
 * was unreliable for these in this CRA SPA). Pagination-driven `noindex` (passed
 * as a prop) always wins so stored SEO can never make /page/2+ indexable.
 */
const SITE_NAME = 'Explora Mauritius';
const PROD_ORIGIN = 'https://explora.mu';
const DEFAULT_DESCRIPTION = 'High-resolution Mauritius photography from talented local photographers. Browse beaches, hotels, nature, wildlife and more.';
const DEFAULT_TITLE = `${SITE_NAME} — Mauritius Photography by Local Photographers`;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const toCanonical = (value) => {
  try {
    const parsed = new URL(value, PROD_ORIGIN);
    return `${PROD_ORIGIN}${parsed.pathname}${parsed.search}`;
  } catch (e) {
    return PROD_ORIGIN;
  }
};

// Merge stored JSON-LD with generated nodes: stored replaces the page-level
// node, but auto-preserve BreadcrumbList and the main-photo ImageObject.
const mergeJsonLd = (stored, generated) => {
  const result = [...stored];
  // Collect @type values, descending into @graph when present (the stored tag
  // payload wraps everything in a single @graph, so its top-level @type is
  // undefined — without this we'd wrongly append a duplicate BreadcrumbList).
  const typesOf = (arr) => arr.flatMap((o) => {
    if (o && Array.isArray(o['@graph'])) {
      return o['@graph'].flatMap((g) => {
        const t = g && g['@type'];
        return Array.isArray(t) ? t : [t];
      });
    }
    const t = o && o['@type'];
    return Array.isArray(t) ? t : [t];
  });
  const storedTypes = typesOf(stored);
  if (!storedTypes.includes('BreadcrumbList')) {
    const bc = generated.find((o) => o && o['@type'] === 'BreadcrumbList');
    if (bc) result.push(bc);
  }
  if (!storedTypes.includes('ImageObject')) {
    const io = generated.find((o) => o && o['@type'] === 'ImageObject');
    if (io) result.push(io);
  }
  return result;
};

const Seo = ({
  title,
  description,
  canonical,
  image,
  imageAlt,
  type = 'website',
  noindex = false,
  nofollow = false,
  jsonLd = [],
}) => {
  const [stored, setStored] = useState(null);
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';

  // Fetch the official stored SEO for this path (Model B).
  useEffect(() => {
    let cancelled = false;
    setStored(null);
    fetch(`${API}/page-seo/resolve?path=${encodeURIComponent(path)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d && d.seo) setStored(d.seo); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [path]);

  // Generated (fallback) values.
  const genFullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const genDesc = (description || DEFAULT_DESCRIPTION).slice(0, 300);
  const genCanonical = toCanonical(canonical || (typeof window !== 'undefined' ? window.location.href : '/'));
  const genImage = image || `${PROD_ORIGIN}/og-default.jpg`;
  const genJsonLd = Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : (jsonLd ? [jsonLd] : []);

  // Official values = stored (when present) else generated.
  const s = stored || {};
  const fullTitle = (s.meta_title || '').trim() || genFullTitle;
  const metaDesc = ((s.meta_description || '').trim() || genDesc).slice(0, 300);
  const canonicalUrl = (s.canonical || '').trim() ? toCanonical(s.canonical) : genCanonical;
  const ogTitle = (s.og_title || '').trim() || (s.meta_title || '').trim() || (title || genFullTitle);
  const ogDesc = (s.og_description || '').trim() || metaDesc;
  const ogUrl = (s.og_url || '').trim() ? toCanonical(s.og_url) : canonicalUrl;
  const ogImage = (s.og_image || '').trim() || genImage;
  const twTitle = (s.twitter_title || '').trim() || ogTitle;
  const twDesc = (s.twitter_description || '').trim() || ogDesc;
  const twImage = (s.twitter_image || '').trim() || ogImage;
  const twCard = (s.twitter_card || '').trim() || 'summary_large_image';

  // robots: pagination prop (noindex) always wins; else stored robots; else none.
  let robotsContent = null;
  if (noindex) {
    robotsContent = `noindex, ${nofollow ? 'nofollow' : 'follow'}`;
  } else if ((s.robots || '').trim()) {
    robotsContent = s.robots.trim();
  }

  // JSON-LD: stored replaces page-level node, keep Breadcrumb + main ImageObject.
  const storedLd = Array.isArray(s.jsonld) ? s.jsonld.filter(Boolean) : [];
  const effectiveLd = storedLd.length > 0 ? mergeJsonLd(storedLd, genJsonLd) : genJsonLd;

  // Title (single <title> from index.html).
  useEffect(() => {
    if (typeof document !== 'undefined') document.title = fullTitle;
    return () => { if (typeof document !== 'undefined') document.title = DEFAULT_TITLE; };
  }, [fullTitle]);

  // Robots — imperative head injection (reliably captured by the prerenderer).
  useEffect(() => {
    if (typeof document === 'undefined' || !robotsContent) return undefined;
    const m = document.createElement('meta');
    m.name = 'robots';
    m.setAttribute('data-seo-robots', 'true');
    m.content = robotsContent;
    document.head.appendChild(m);
    return () => m.remove();
  }, [robotsContent]);

  // Remove the static fallback <meta name="description"> (marked data-static in
  // index.html) once React runs, so only Helmet's specific description remains.
  // The static tag stays the fallback for pre-JS / non-crawler fetchers.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document
      .querySelectorAll('meta[name="description"][data-static]')
      .forEach((m) => m.remove());
  }, [metaDesc]);

  // JSON-LD — imperative head injection.
  const ldSerialized = JSON.stringify(effectiveLd);
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const list = JSON.parse(ldSerialized);
    const nodes = list.map((obj) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-seo-jsonld', 'true');
      el.text = JSON.stringify(obj);
      document.head.appendChild(el);
      return el;
    });
    return () => nodes.forEach((n) => n.remove());
  }, [ldSerialized]);

  return (
    <Helmet>
      <meta name="description" content={metaDesc} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDesc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:image" content={ogImage} />
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}

      <meta name="twitter:card" content={twCard} />
      <meta name="twitter:title" content={twTitle} />
      <meta name="twitter:description" content={twDesc} />
      <meta name="twitter:image" content={twImage} />
      {imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}
    </Helmet>
  );
};

export default Seo;
